/* nns-finder.js — No Nonsense Shop "Product Finder"
 * Vanilla, ES5-safe JS. No frameworks, no build step, no external libraries
 * (the store CSP blocks them). Self-contained: injects its own <style>,
 * a homepage launcher, and a modal with TWO routes from broad -> shortlist:
 *
 *   ROUTE A  "Help me choose"  (AI-GUIDED)  — one leading question at a time,
 *            3 steps (category -> type -> budget), ends in a recommended shortlist.
 *   ROUTE B  "Browse by category" (USER-SELECTED) — pick a broad category,
 *            reveal its sub-types, drill straight to products.
 *
 * The default storefront is untouched; the finder narrows ON DEMAND only.
 *
 * ROUTING (verified on the live store — used EXACTLY, no invented params):
 *   - category only (no type):  "/collections/" + handle
 *        ; if a budget is set, append "?" + priceParams
 *   - a type is chosen:         "/search?q=" + encodeURIComponent(type.q) + "&type=product"
 *        ; if a budget is set, append "&" + priceParams
 *   - priceParams = the "&"-join of:
 *        budget.gte ? "filter.v.price.gte=" + budget.gte : ""
 *        budget.lte ? "filter.v.price.lte=" + budget.lte : ""
 *   NOTE: the product-type FILTER (filter.p.product_type) is NOT enabled on this
 *   store, so a chosen type routes through SEARCH, not a collection facet. The
 *   Price + Availability facets ARE enabled, so price params work on both
 *   collection and search result pages.
 *
 * Idempotent (id-guarded, never double-injects, survives re-renders via a short
 * retry like nns-global). Mobile-first, NNS navy/gold, all CSS scoped under
 * the ".nnsf-" prefix so nothing leaks. Respects prefers-reduced-motion.
 */
(function () {
  "use strict";

  /* Guard against double-execution (asset referenced twice / editor reloads). */
  if (window.__nnsFinderLoaded) return;
  window.__nnsFinderLoaded = true;

  /* ===================== DATA TREE (verbatim) ===================== */

  var CATS = [
    { key: 'bikes', label: 'Bikes & Scooters', handle: 'electric-bikes-scooters', icon: 'bike', types: [
      { label: 'Electric bikes', q: 'electric bike' }, { label: 'Electric scooters', q: 'electric scooter' }, { label: 'Kids ride-ons', q: 'kids ride on' } ] },
    { key: 'computers', label: 'Computers & Monitors', handle: 'computers-monitors', icon: 'device-desktop', types: [
      { label: 'Laptops', q: 'laptop' }, { label: 'Mini PCs', q: 'mini pc' }, { label: 'Monitors', q: 'monitor' }, { label: 'Mice & accessories', q: 'mouse' } ] },
    { key: 'phones', label: 'Phones & Tablets', handle: 'phones-tablets', icon: 'device-mobile', types: [
      { label: 'Smartphones', q: 'smartphone' }, { label: 'Tablets', q: 'tablet' } ] },
    { key: 'tv', label: 'TVs, Audio & Cameras', handle: 'tv-audio-cameras', icon: 'device-tv', types: [
      { label: 'TVs', q: 'smart tv' }, { label: 'Cameras', q: 'camera' }, { label: 'Projectors', q: 'projector' }, { label: 'TV stands & mounts', q: 'tv stand' } ] },
    { key: 'home', label: 'Home & Kitchen', handle: 'home-kitchen-appliances', icon: 'home', types: [
      { label: 'Vacuums', q: 'vacuum cleaner' }, { label: 'Robot vacuums', q: 'robot vacuum' }, { label: 'Air conditioners', q: 'air conditioner' }, { label: 'Air fryers', q: 'air fryer' }, { label: 'Fans', q: 'pedestal fan' }, { label: 'Kettles', q: 'kettle' } ] },
    { key: 'furniture', label: 'Furniture & Storage', handle: 'furniture-storage', icon: 'sofa', types: [
      { label: 'Desks', q: 'desk' }, { label: 'Coffee tables', q: 'coffee table' }, { label: 'Shelving', q: 'shelving unit' }, { label: 'Shoe racks', q: 'shoe rack' }, { label: 'Cabinets', q: 'cabinet' } ] },
    { key: 'toys', label: 'Toys & Kids', handle: 'toys-kids', icon: 'mood-kid', types: [
      { label: 'Toys', q: 'toy' }, { label: 'Kids ride-ons', q: 'kids ride on car' } ] },
    { key: 'accessories', label: 'Accessories', handle: 'accessories-essentials', icon: 'plug', types: [
      { label: 'Chargers', q: 'charger' }, { label: 'Power strips', q: 'power strip' }, { label: 'Travel adapters', q: 'travel adapter' } ] }
  ];

  var BUDGETS = [
    { label: 'Under £50', lte: 50 },
    { label: '£50–£150', gte: 50, lte: 150 },
    { label: '£150–£400', gte: 150, lte: 400 },
    { label: '£400+', gte: 400 },
    { label: 'Any budget' }
  ];

  /* ===================== tiny helpers ===================== */

  function $(sel, root) { return (root || document).querySelector(sel); }
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  function on(node, ev, fn) { if (node && node.addEventListener) node.addEventListener(ev, fn, false); }
  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  /* Subtle inline icons. Drawn simply; no icon-font dependency. */
  function icon(name) {
    var p = {
      bike: '<circle cx="6" cy="17" r="3.2"/><circle cx="18" cy="17" r="3.2"/><path d="M6 17l4-7h5l3 7M9 7h3"/>',
      'device-desktop': '<rect x="3" y="4" width="18" height="12" rx="1.5"/><path d="M9 20h6M12 16v4"/>',
      'device-mobile': '<rect x="7" y="3" width="10" height="18" rx="2"/><path d="M11 18h2"/>',
      'device-tv': '<rect x="3" y="5" width="18" height="12" rx="1.5"/><path d="M8 21h8"/>',
      home: '<path d="M4 11l8-7 8 7M6 10v9h12v-9"/>',
      sofa: '<path d="M4 11V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3M3 11a2 2 0 0 1 2 2v3h14v-3a2 2 0 0 1 2-2M5 16v2M19 16v2"/>',
      'mood-kid': '<circle cx="12" cy="12" r="8.5"/><path d="M9 10h.01M15 10h.01M9 15c1 1 5 1 6 0"/>',
      plug: '<path d="M9 3v5M15 3v5M7 8h10v3a5 5 0 0 1-10 0V8zM12 16v5"/>'
    };
    return '<svg class="nnsf-ic" viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" focusable="false">' +
      (p[name] || p.home) + '</svg>';
  }

  /* ===================== ROUTING ===================== */

  /* Build the "&"-joined price params for a chosen budget (or "" for none/any). */
  function priceParams(budget) {
    if (!budget) return "";
    var parts = [];
    if (budget.gte) parts.push("filter.v.price.gte=" + budget.gte);
    if (budget.lte) parts.push("filter.v.price.lte=" + budget.lte);
    return parts.join("&");
  }

  /* Resolve {cat, type, budget} -> a real store URL using the verified rules. */
  function buildUrl(cat, type, budget) {
    var pp = priceParams(budget);
    if (type) {
      // A type was chosen -> SEARCH (product-type collection filter isn't enabled).
      var u = "/search?q=" + encodeURIComponent(type.q) + "&type=product";
      if (pp) u += "&" + pp;
      return u;
    }
    // Category only -> the collection page; price (if any) goes on as a query.
    var c = "/collections/" + cat.handle;
    if (pp) c += "?" + pp;
    return c;
  }

  function budgetPhrase(budget) {
    if (!budget || (budget.gte == null && budget.lte == null)) return "";
    return "(" + budget.label + ")";
  }

  /* ===================== STYLES (scoped .nnsf-) ===================== */

  function injectStyles() {
    if (document.getElementById("nnsf-styles")) return;
    var css = [
      ":root{--nnsf-navy:#16223F;--nnsf-gold:#EAB01D;--nnsf-ink:#16223F;",
      "--nnsf-line:#e4e7ee;--nnsf-soft:#f5f6f9;--nnsf-white:#fff;--nnsf-mut:#5b6276;}",

      /* launcher banner */
      ".nnsf-launch{box-sizing:border-box;display:flex;align-items:center;gap:14px;",
      "flex-wrap:wrap;justify-content:center;text-align:center;margin:14px auto;",
      "max-width:1100px;padding:16px 20px;border:1px solid var(--nnsf-line);",
      "border-radius:16px;background:var(--nnsf-soft);}",
      ".nnsf-launch__txt{font-size:1.02rem;font-weight:600;color:var(--nnsf-navy);}",
      ".nnsf-launch__btn{cursor:pointer;border:0;border-radius:999px;padding:11px 22px;",
      "font:inherit;font-weight:700;font-size:.98rem;color:var(--nnsf-navy);",
      "background:var(--nnsf-gold);transition:transform .15s ease,box-shadow .15s ease;}",
      ".nnsf-launch__btn:hover{box-shadow:0 6px 16px rgba(22,34,63,.18);transform:translateY(-1px);}",
      /* phone: thin single-row banner (don't wrap text above button) */
      "@media(max-width:749px){.nnsf-launch{flex-wrap:nowrap;gap:9px;padding:8px 12px;margin:9px auto;border-radius:12px;text-align:left;}",
      ".nnsf-launch__txt{font-size:.82rem;flex:1;line-height:1.2;}",
      ".nnsf-launch__btn{padding:7px 13px;font-size:.78rem;white-space:nowrap;flex:0 0 auto;}}",

      /* overlay + panel */
      ".nnsf-ov{position:fixed;inset:0;z-index:99999;display:flex;align-items:flex-start;",
      "justify-content:center;padding:24px 14px;overflow-y:auto;",
      "background:rgba(13,20,40,.55);-webkit-overflow-scrolling:touch;}",
      /* ROOT-CAUSE FIX: the [hidden] attribute alone can't hide .nnsf-ov because the
         author rule above (display:flex) outranks the UA [hidden]{display:none} rule,
         so the overlay stayed visible and could never be dismissed. This makes the
         hidden state actually hide it (and pointer-events:none stops it eating taps). */
      ".nnsf-ov[hidden]{display:none !important;pointer-events:none;}",
      ".nnsf-panel{box-sizing:border-box;width:100%;max-width:680px;margin:auto;",
      "background:var(--nnsf-white);border-radius:20px;",
      "box-shadow:0 24px 60px rgba(13,20,40,.32);overflow:hidden;}",
      ".nnsf-head{display:flex;align-items:flex-start;gap:12px;padding:22px 24px 6px;}",
      ".nnsf-title{margin:0;font-size:1.32rem;line-height:1.2;color:var(--nnsf-navy);font-weight:800;flex:1;}",
      ".nnsf-x{flex:0 0 auto;cursor:pointer;border:1px solid var(--nnsf-line);background:var(--nnsf-white);",
      "color:var(--nnsf-navy);width:38px;height:38px;border-radius:50%;font-size:20px;line-height:1;",
      "display:flex;align-items:center;justify-content:center;}",
      ".nnsf-x:hover{background:var(--nnsf-soft);}",
      ".nnsf-body{padding:8px 24px 26px;}",
      ".nnsf-sub{margin:2px 0 18px;color:var(--nnsf-mut);font-size:.97rem;}",

      /* progress / steps bar */
      ".nnsf-bar{display:flex;align-items:center;justify-content:space-between;",
      "gap:12px;margin:0 0 16px;}",
      ".nnsf-step{font-size:.85rem;font-weight:700;color:var(--nnsf-mut);letter-spacing:.02em;}",
      ".nnsf-dots{display:flex;gap:6px;}",
      ".nnsf-dot{width:8px;height:8px;border-radius:50%;background:var(--nnsf-line);}",
      ".nnsf-dot.is-on{background:var(--nnsf-gold);}",

      /* choice/category card grid */
      ".nnsf-grid{display:grid;gap:12px;grid-template-columns:1fr;}",
      "@media(min-width:520px){.nnsf-grid{grid-template-columns:1fr 1fr;}}",
      ".nnsf-card{display:flex;align-items:center;gap:14px;text-align:left;width:100%;",
      "box-sizing:border-box;cursor:pointer;font:inherit;color:var(--nnsf-navy);",
      "background:var(--nnsf-white);border:1px solid var(--nnsf-line);border-radius:16px;",
      "padding:16px 18px;transition:border-color .15s ease,box-shadow .15s ease,transform .15s ease;}",
      ".nnsf-card:hover{border-color:var(--nnsf-gold);box-shadow:0 8px 22px rgba(22,34,63,.12);transform:translateY(-1px);}",
      ".nnsf-card .nnsf-ic{flex:0 0 auto;color:var(--nnsf-navy);}",
      ".nnsf-card__t{font-weight:800;font-size:1.04rem;}",
      ".nnsf-card__s{display:block;margin-top:2px;font-weight:500;font-size:.88rem;color:var(--nnsf-mut);}",
      ".nnsf-card--big{flex-direction:column;align-items:flex-start;text-align:left;padding:22px;gap:8px;}",
      ".nnsf-card--big .nnsf-card__t{font-size:1.12rem;}",

      /* option / type chips */
      ".nnsf-chips{display:flex;flex-wrap:wrap;gap:10px;margin:4px 0 4px;}",
      ".nnsf-chip{cursor:pointer;font:inherit;font-weight:600;font-size:.95rem;color:var(--nnsf-navy);",
      "background:var(--nnsf-white);border:1px solid var(--nnsf-line);border-radius:999px;",
      "padding:10px 16px;transition:border-color .15s ease,background .15s ease;}",
      ".nnsf-chip:hover{border-color:var(--nnsf-gold);background:var(--nnsf-soft);}",
      ".nnsf-chip--ghost{color:var(--nnsf-mut);border-style:dashed;}",

      /* footer row (Back / Skip / CTA) */
      ".nnsf-foot{display:flex;align-items:center;gap:12px;margin-top:20px;flex-wrap:wrap;}",
      ".nnsf-spacer{flex:1;}",
      ".nnsf-link{cursor:pointer;border:0;background:none;font:inherit;font-weight:700;",
      "color:var(--nnsf-mut);padding:8px 6px;}",
      ".nnsf-link:hover{color:var(--nnsf-navy);}",
      ".nnsf-back{cursor:pointer;border:1px solid var(--nnsf-line);background:var(--nnsf-white);",
      "font:inherit;font-weight:700;color:var(--nnsf-navy);border-radius:999px;padding:10px 18px;}",
      ".nnsf-back:hover{background:var(--nnsf-soft);}",
      ".nnsf-cta{cursor:pointer;border:0;border-radius:999px;padding:13px 26px;font:inherit;",
      "font-weight:800;font-size:1rem;color:var(--nnsf-navy);background:var(--nnsf-gold);",
      "text-decoration:none;display:inline-block;transition:transform .15s ease,box-shadow .15s ease;}",
      ".nnsf-cta:hover{box-shadow:0 8px 20px rgba(22,34,63,.22);transform:translateY(-1px);}",

      /* result + browse-detail blocks */
      ".nnsf-result{font-size:1.06rem;line-height:1.5;color:var(--nnsf-navy);margin:6px 0 4px;}",
      ".nnsf-result b{color:var(--nnsf-navy);}",
      ".nnsf-viewall{display:inline-block;margin-top:6px;font-weight:700;color:var(--nnsf-navy);}",
      ".nnsf-viewall:hover{color:var(--nnsf-gold);}",
      ".nnsf-catname{display:flex;align-items:center;gap:10px;margin:0 0 12px;font-weight:800;",
      "font-size:1.1rem;color:var(--nnsf-navy);}",

      /* reduced motion */
      "@media(prefers-reduced-motion:reduce){.nnsf-launch__btn,.nnsf-card,.nnsf-cta{transition:none;}",
      ".nnsf-launch__btn:hover,.nnsf-card:hover,.nnsf-cta:hover{transform:none;}}",

      /* body scroll lock */
      "body.nnsf-locked{overflow:hidden;}"
    ].join("");
    var style = el("style");
    style.id = "nnsf-styles";
    style.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(style);
  }

  /* ===================== MODAL CORE ===================== */

  var overlay = null;      // the .nnsf-ov wrapper (built once, reused)
  var panel = null;        // the scrollable dialog panel
  var bodyEl = null;       // panel body we render screens into
  var titleEl = null;      // the dialog heading (aria-labelledby target)
  var lastFocus = null;    // element to restore focus to on close

  /* Per-session selection state for ROUTE A. */
  var sel = { cat: null, type: null, budget: null };

  function buildModal() {
    if (overlay) return;
    overlay = el("div", "nnsf-ov");
    overlay.id = "nnsf-overlay";
    overlay.setAttribute("hidden", "");

    panel = el("div", "nnsf-panel");
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-modal", "true");
    panel.setAttribute("aria-labelledby", "nnsf-title");

    var head = el("div", "nnsf-head");
    titleEl = el("h2", "nnsf-title", "Find your perfect product");
    titleEl.id = "nnsf-title";
    var x = el("button", "nnsf-x", "&times;");
    x.type = "button";
    x.setAttribute("aria-label", "Close");
    on(x, "click", close);
    head.appendChild(titleEl);
    head.appendChild(x);

    bodyEl = el("div", "nnsf-body");

    panel.appendChild(head);
    panel.appendChild(bodyEl);
    overlay.appendChild(panel);

    /* Backdrop tap/click closes; interactions inside the panel do not.
       Listen on the overlay itself and only close when the event target IS the
       overlay (i.e. the dark backdrop), never a descendant of the panel. We bind
       both "click" and "pointerup" so a tap on a touch screen is reliable. */
    function onBackdrop(e) { if (e.target === overlay) { e.preventDefault(); close(); } }
    on(overlay, "click", onBackdrop);
    on(overlay, "pointerup", onBackdrop);
    /* Stop taps/clicks that land on the panel from ever reaching the overlay. */
    on(panel, "click", function (e) { e.stopPropagation(); });
    on(panel, "pointerup", function (e) { e.stopPropagation(); });

    document.body.appendChild(overlay);
  }

  /* Focus trap: keep Tab inside the panel; Esc closes. */
  function onKeydown(e) {
    if (!overlay || overlay.hasAttribute("hidden")) return;
    if (e.key === "Escape" || e.keyCode === 27) { e.preventDefault(); close(); return; }
    if (e.key === "Tab" || e.keyCode === 9) {
      var f = panel.querySelectorAll(
        'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  function open() {
    buildModal();
    lastFocus = document.activeElement;
    sel = { cat: null, type: null, budget: null };
    overlay.removeAttribute("hidden");
    overlay.style.display = "flex";   /* explicit show (independent of CSS cascade) */
    document.body.classList.add("nnsf-locked");
    document.addEventListener("keydown", onKeydown, false);
    renderEntry();
    panel.scrollTop = 0;
    overlay.scrollTop = 0;
  }

  function close() {
    if (!overlay || overlay.hasAttribute("hidden")) return;
    overlay.setAttribute("hidden", "");
    overlay.style.display = "none";   /* explicit hide (independent of CSS cascade) */
    document.body.classList.remove("nnsf-locked");
    document.removeEventListener("keydown", onKeydown, false);
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
  }

  /* Clear the body and set the heading; focus the heading for screen readers. */
  function screen(title) {
    titleEl.textContent = title;
    bodyEl.innerHTML = "";
    if (overlay) overlay.scrollTop = 0;
  }

  /* Move keyboard focus to the first interactive element of a screen. */
  function focusFirst() {
    var f = bodyEl.querySelector('button,a[href],[tabindex]:not([tabindex="-1"])');
    if (f) { try { f.focus(); } catch (e) {} }
  }

  /* ===================== ENTRY SCREEN ===================== */

  function renderEntry() {
    screen("Find your perfect product");
    bodyEl.appendChild(el("p", "nnsf-sub", "Two easy ways to get to the right thing."));

    var grid = el("div", "nnsf-grid");

    var a = el("button", "nnsf-card nnsf-card--big",
      icon("mood-kid") +
      '<span><span class="nnsf-card__t">Tell us what you need</span>' +
      '<span class="nnsf-card__s">Type it &mdash; we&rsquo;ll find it for you</span></span>');
    a.type = "button";
    on(a, "click", function () { sel = { cat: null, type: null, budget: null }; routeA_text(); });

    var b = el("button", "nnsf-card nnsf-card--big",
      icon("home") +
      '<span><span class="nnsf-card__t">Browse by category</span>' +
      '<span class="nnsf-card__s">Pick a category and explore</span></span>');
    b.type = "button";
    on(b, "click", function () { routeB_cats(); });

    grid.appendChild(a);
    grid.appendChild(b);
    bodyEl.appendChild(grid);
    focusFirst();
  }

  /* progress bar: 1-based step index of total. */
  function progressBar(step, total) {
    var bar = el("div", "nnsf-bar");
    bar.appendChild(el("span", "nnsf-step", "Step " + step + " of " + total));
    var dots = el("div", "nnsf-dots");
    for (var i = 1; i <= total; i++) {
      dots.appendChild(el("span", "nnsf-dot" + (i <= step ? " is-on" : "")));
    }
    bar.appendChild(dots);
    return bar;
  }

  /* footer with optional Back + Skip; returns the row so caller can add a CTA. */
  function footer(backFn, skipFn) {
    var foot = el("div", "nnsf-foot");
    if (backFn) {
      var b = el("button", "nnsf-back", "&larr; Back");
      b.type = "button"; on(b, "click", backFn); foot.appendChild(b);
    }
    foot.appendChild(el("span", "nnsf-spacer"));
    if (skipFn) {
      var s = el("button", "nnsf-link", "Skip");
      s.type = "button"; on(s, "click", skipFn); foot.appendChild(s);
    }
    return foot;
  }

  /* ===================== ROUTE A — describe it (free text + smart search) ===================== */

  /* Lightweight intent parse: pull a budget out of the free text + clean the query. */
  function aiParse(text) {
    var t = " " + (text || "").toLowerCase() + " ";
    var budget = null, m;
    if ((m = t.match(/(?:under|below|less than|up to|max|cheaper than|<)\s*£?\s*(\d{2,4})/))) budget = { lte: parseInt(m[1], 10) };
    else if ((m = t.match(/(?:over|above|more than|at least|from|>)\s*£?\s*(\d{2,4})/))) budget = { gte: parseInt(m[1], 10) };
    else if ((m = t.match(/£?\s*(\d{2,4})\s*(?:-|to|–|and)\s*£?\s*(\d{2,4})/))) budget = { gte: parseInt(m[1], 10), lte: parseInt(m[2], 10) };
    else if (/\b(cheap|budget|affordable|bargain)\b/.test(t)) budget = { lte: 60 };
    else if (/\b(premium|high.?end|top.?end)\b/.test(t)) budget = { gte: 250 };
    var q = (text || "")
      .replace(/(?:under|below|less than|up to|max|over|above|more than|at least|around|about|cheaper than)\s*£?\s*\d{2,4}/gi, " ")
      .replace(/£?\s*\d{2,4}\s*(?:-|to|–|and)\s*£?\s*\d{2,4}/gi, " ")
      .replace(/[£$]/g, " ")
      .replace(/\b(i|im|need|want|looking|for|an?|the|my|please|some|good|nice|new|with|find|me|to|buy|get)\b/gi, " ")
      .replace(/\s+/g, " ").trim();
    return { q: q || (text || "").trim(), budget: budget };
  }

  /* Route A screen: a free-text box + optional budget, then a smart search. */
  function routeA_text() {
    screen("What are you looking for?");
    bodyEl.appendChild(el("p", "nnsf-sub", "Describe it in your own words — we’ll find the closest matches."));

    var input = document.createElement("textarea");
    input.rows = 2;
    input.setAttribute("placeholder", "e.g. a quiet robot vacuum under £200");
    input.setAttribute("aria-label", "Describe what you are looking for");
    input.style.cssText = "width:100%;box-sizing:border-box;border:1.5px solid #d7dbe4;border-radius:12px;padding:12px 14px;font:inherit;font-size:1rem;line-height:1.4;color:#16223F;resize:none;outline:none;margin:2px 0 12px;";
    input.onfocus = function () { input.style.borderColor = "#16223F"; };
    input.onblur = function () { input.style.borderColor = "#d7dbe4"; };
    bodyEl.appendChild(input);

    bodyEl.appendChild(el("p", "nnsf-sub", "Budget (optional)"));
    var chips = el("div", "nnsf-chips");
    var chosen = { b: null };
    for (var i = 0; i < BUDGETS.length; i++) {
      (function (bg) {
        var isAny = (bg.gte == null && bg.lte == null);
        var chip = el("button", "nnsf-chip" + (isAny ? " nnsf-chip--ghost" : ""), esc(bg.label));
        chip.type = "button";
        on(chip, "click", function () {
          var sib = chips.querySelectorAll(".nnsf-chip");
          for (var k = 0; k < sib.length; k++) { sib[k].style.background = ""; sib[k].style.color = ""; }
          chip.style.background = "#16223F"; chip.style.color = "#fff";
          chosen.b = isAny ? null : bg;
        });
        chips.appendChild(chip);
      })(BUDGETS[i]);
    }
    bodyEl.appendChild(chips);

    var go = el("button", "nnsf-cta", "Find it &rarr;");
    go.type = "button";
    function submit() {
      var parsed = aiParse(input.value);
      if (chosen.b) parsed.budget = chosen.b;        /* explicit budget chip overrides the text */
      if (!parsed.q) { try { input.focus(); } catch (e) {} return; }
      routeA_textResult(parsed);
    }
    on(go, "click", submit);
    on(input, "keydown", function (e) { if ((e.key === "Enter" || e.keyCode === 13) && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } });
    var row = el("div", "nnsf-ctarow"); row.appendChild(go); bodyEl.appendChild(row);

    bodyEl.appendChild(footer(renderEntry, null));
    try { input.focus(); } catch (e) {}
  }

  /* Route A result: friendly "AI" line + a primary CTA routing to the search. */
  function routeA_textResult(parsed) {
    screen("Here’s your shortlist");
    var phrase = budgetPhrase(parsed.budget);
    var line = "On it — finding " + esc(parsed.q) + (phrase ? " " + esc(phrase) : "") + ". Here’s your shortlist:";
    bodyEl.appendChild(el("p", "nnsf-result", line));
    var url = "/search?q=" + encodeURIComponent(parsed.q) + "&type=product";
    if (parsed.budget) { var pp = priceParams(parsed.budget); if (pp) url += "&" + pp; }
    var cta = el("a", "nnsf-cta", "View matches &rarr;");
    cta.setAttribute("href", url);
    var row = el("div", "nnsf-ctarow"); row.appendChild(cta); bodyEl.appendChild(row);
    bodyEl.appendChild(footer(routeA_text, null));
    focusFirst();
  }

  /* Q2: what type? (chosen category's chips + "Any type"). */
  function routeA_q2() {
    if (!sel.cat) { routeA_q3(); return; }
    screen("What type of " + sel.cat.label.toLowerCase() + "?");
    bodyEl.appendChild(progressBar(2, 3));
    bodyEl.appendChild(el("p", "nnsf-sub", "Choose a type, or keep it broad."));

    var chips = el("div", "nnsf-chips");
    for (var i = 0; i < sel.cat.types.length; i++) {
      (function (t) {
        var chip = el("button", "nnsf-chip", esc(t.label));
        chip.type = "button";
        on(chip, "click", function () { sel.type = t; routeA_q3(); });
        chips.appendChild(chip);
      })(sel.cat.types[i]);
    }
    var any = el("button", "nnsf-chip nnsf-chip--ghost", "Any type");
    any.type = "button";
    on(any, "click", function () { sel.type = null; routeA_q3(); });
    chips.appendChild(any);
    bodyEl.appendChild(chips);

    bodyEl.appendChild(footer(routeA_q1, function () { sel.type = null; routeA_q3(); }));
    focusFirst();
  }

  /* Q3: budget? (BUDGETS + "Any budget"). */
  function routeA_q3() {
    screen("What's your budget?");
    bodyEl.appendChild(progressBar(3, 3));
    bodyEl.appendChild(el("p", "nnsf-sub", "We'll only show what fits."));

    var chips = el("div", "nnsf-chips");
    for (var i = 0; i < BUDGETS.length; i++) {
      (function (bg) {
        var isAny = (bg.gte == null && bg.lte == null);
        var chip = el("button", "nnsf-chip" + (isAny ? " nnsf-chip--ghost" : ""), esc(bg.label));
        chip.type = "button";
        on(chip, "click", function () { sel.budget = isAny ? null : bg; routeA_result(); });
        chips.appendChild(chip);
      })(BUDGETS[i]);
    }
    bodyEl.appendChild(chips);

    /* Back goes to Q2 if a category exists, else Q1. Skip = no budget. */
    var back = sel.cat ? routeA_q2 : routeA_q1;
    bodyEl.appendChild(footer(back, function () { sel.budget = null; routeA_result(); }));
    focusFirst();
  }

  /* RESULT: friendly line + primary "View matches" CTA + "Start over". */
  function routeA_result() {
    screen("Here's your shortlist");

    var noun = sel.type ? sel.type.label.toLowerCase()
      : (sel.cat ? sel.cat.label.toLowerCase() : "products");
    var phrase = budgetPhrase(sel.budget);
    var line = "Great — here's your shortlist of " + esc(noun);
    if (phrase) line += " " + esc(phrase);
    line += ".";
    bodyEl.appendChild(el("p", "nnsf-result", line));

    var url = sel.cat ? buildUrl(sel.cat, sel.type, sel.budget)
      : (sel.type ? buildUrl(null, sel.type, sel.budget) : "/collections/all");
    // If user skipped everything, fall back to the broad catalogue.
    if (!sel.cat && !sel.type) {
      url = "/collections/all";
      if (sel.budget) url += "?" + priceParams(sel.budget);
    }

    var foot = el("div", "nnsf-foot");
    var start = el("button", "nnsf-link", "Start over");
    start.type = "button";
    on(start, "click", function () { sel = { cat: null, type: null, budget: null }; renderEntry(); });
    foot.appendChild(start);
    foot.appendChild(el("span", "nnsf-spacer"));

    var cta = el("a", "nnsf-cta", "View matches &rarr;");
    cta.setAttribute("href", url);
    foot.appendChild(cta);

    bodyEl.appendChild(foot);
    // Focus the primary action.
    try { cta.focus(); } catch (e) {}
  }

  /* ===================== ROUTE B — browse ===================== */

  /* Grid of the 8 categories; clicking one reveals its sub-types in place. */
  function routeB_cats() {
    screen("Browse by category");
    bodyEl.appendChild(el("p", "nnsf-sub", "Pick a category to see what's inside."));

    var grid = el("div", "nnsf-grid");
    for (var i = 0; i < CATS.length; i++) {
      (function (cat) {
        var c = el("button", "nnsf-card",
          icon(cat.icon) + '<span class="nnsf-card__t">' + esc(cat.label) + '</span>');
        c.type = "button";
        on(c, "click", function () { routeB_detail(cat); });
        grid.appendChild(c);
      })(CATS[i]);
    }
    bodyEl.appendChild(grid);
    bodyEl.appendChild(footer(renderEntry, null));
    focusFirst();
  }

  /* A category's sub-types + a "View all in {category}" collection link. */
  function routeB_detail(cat) {
    screen(cat.label);

    var name = el("div", "nnsf-catname", icon(cat.icon) + "<span>" + esc(cat.label) + "</span>");
    bodyEl.appendChild(name);
    bodyEl.appendChild(el("p", "nnsf-sub", "Choose a type, or view everything in this category."));

    var chips = el("div", "nnsf-chips");
    for (var i = 0; i < cat.types.length; i++) {
      (function (t) {
        var chip = el("a", "nnsf-chip", esc(t.label));
        // Sub-type -> SEARCH url (no budget in browse mode).
        chip.setAttribute("href", buildUrl(cat, t, null));
        chips.appendChild(chip);
      })(cat.types[i]);
    }
    bodyEl.appendChild(chips);

    // "View all" -> the collection page for this category.
    var all = el("a", "nnsf-viewall", "View all in " + esc(cat.label) + " &rarr;");
    all.setAttribute("href", buildUrl(cat, null, null));
    bodyEl.appendChild(all);

    bodyEl.appendChild(footer(routeB_cats, null));
    focusFirst();
  }

  /* ===================== LAUNCHER (homepage only) ===================== */

  function isHomepage() {
    var p = location.pathname || "";
    if (p === "/" || p === "") return true;
    // Horizon often tags the index template on <body> / a section.
    if (document.body && /template-index|index-template/.test(document.body.className || "")) return true;
    if ($("[data-template='index'], .template-index, #shopify-section-index, .index-section")) return true;
    return false;
  }

  function injectLauncher() {
    if (!isHomepage()) return false;
    if (document.getElementById("nnsf-launch")) return true; // already there

    var main = document.getElementById("MainContent") || $("main");
    if (!main) return false;

    var launch = el("div", "nnsf-launch");
    launch.id = "nnsf-launch";
    launch.appendChild(el("span", "nnsf-launch__txt", "Help finding your item?"));
    var btn = el("button", "nnsf-launch__btn", "Find your perfect product &rarr;");
    btn.type = "button";
    on(btn, "click", open);
    launch.appendChild(btn);

    /* Insert as an early child of <main>, AFTER any existing .nns-mtabs nav. */
    var mtabs = main.querySelector(".nns-mtabs");
    if (mtabs && mtabs.parentNode === main) {
      if (mtabs.nextSibling) main.insertBefore(launch, mtabs.nextSibling);
      else main.appendChild(launch);
    } else if (mtabs) {
      // .nns-mtabs nested deeper — still place launcher near the top of main.
      main.insertBefore(launch, main.firstChild);
    } else {
      main.insertBefore(launch, main.firstChild);
    }
    return true;
  }

  /* ===================== BOOT ===================== */

  /* Expose a programmatic opener for other elements. */
  window.nnsFinderOpen = function () { injectStyles(); open(); };

  function boot() {
    // The launcher only ever appears on the homepage. On every other page the
    // retry loop below would poll for ~5s doing isHomepage()/DOM lookups that can
    // never succeed — so bail early there. Styles are injected lazily by
    // nnsFinderOpen() when the modal is actually opened, so non-home pages stay
    // free of this ~3KB <style> block too.
    if (!isHomepage()) return;
    injectStyles();
    // Try the launcher now and retry briefly — the homepage hero/sections can
    // render late (same pattern nns-global uses for header re-renders).
    if (injectLauncher()) return;
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      if (injectLauncher() || tries >= 20) clearInterval(timer); // ~5s @ 250ms
    }, 250);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, false);
  } else {
    boot();
  }
})();
